import { createServerFn } from "@tanstack/react-start";

export type EnquiryResult = { ok: true } | { error: string };

/**
 * Public enquiry form on /business. Runs unauthenticated, so everything is
 * validated here and written with the admin client, which also lets us notify
 * admin accounts in-app.
 */
export const submitBusinessEnquiry = createServerFn({ method: "POST" })
  .inputValidator(
    (data: {
      name: string;
      email: string;
      company: string;
      teamSize?: string;
      message?: string;
      planInterest?: string;
    }) => {
      const name = data.name.trim();
      const email = data.email.trim().toLowerCase();
      const company = data.company.trim();
      if (name.length < 2 || name.length > 120) throw new Error("Enter your name");
      if (email.length > 255 || !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
        throw new Error("Enter a valid work email address");
      }
      if (company.length < 2 || company.length > 160) throw new Error("Enter your company");
      return {
        name,
        email,
        company,
        teamSize: (data.teamSize ?? "").trim().slice(0, 40),
        message: (data.message ?? "").trim().slice(0, 2000),
        planInterest: (data.planInterest ?? "").trim().slice(0, 40),
      };
    },
  )
  .handler(async ({ data }): Promise<EnquiryResult> => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const { error } = await supabaseAdmin.from("business_enquiries").insert({
      name: data.name,
      email: data.email,
      company: data.company,
      team_size: data.teamSize || null,
      message: data.message || null,
      plan_interest: data.planInterest || null,
    });
    if (error) return { error: "That didn't send. Please try again." };

    const { data: admins } = await supabaseAdmin
      .from("user_roles")
      .select("user_id")
      .eq("role", "admin");

    if (admins?.length) {
      await supabaseAdmin.from("notifications").insert(
        admins.map((admin) => ({
          user_id: admin.user_id,
          kind: "business_enquiry",
          title: `New business enquiry — ${data.company}`,
          body: `${data.name} (${data.email})${data.teamSize ? `, ${data.teamSize} people` : ""}${
            data.planInterest ? `, interested in ${data.planInterest}` : ""
          }`,
        })),
      );
    }

    return { ok: true };
  });
